import { Cap, Proposal, Sow } from "../capTypes";
import {
  capProposalHasSolarAndBattery,
  hasCapSowAnnualSelfSupply,
  hasCapSowOriginalSelfSupply,
} from "./capSelfSupplyPresentation";
import {
  evaluateSimplifiedPresentationGate,
  simplifiedExperienceIneligible,
  type SimplifiedExperienceDecision,
} from "utils/simplifiedExperienceDecision";
import type { SelfSupplyEligibleMarket } from "utils/selfSupplyEligibility";

type EvaluateCapSimplifiedExperienceArgs = {
  offerReviewSimplifiedEnabled: boolean;
  presentationTargets: SelfSupplyEligibleMarket[];
  createdDateCutoff?: string | undefined;
  state: string | undefined;
  utility: string | undefined;
  sow: Sow | undefined;
  activeProposal: Proposal | undefined;
  cap: Cap | undefined;
  isNonSolar: boolean;
  isOnlyBattery: boolean;
  isOnlyEvCharger: boolean;
  additionalSystem?: boolean;
};

const getCapIsChangeOrder = ({
  sow,
  cap,
  activeProposal,
}: Pick<
  EvaluateCapSimplifiedExperienceArgs,
  "sow" | "cap" | "activeProposal"
>): boolean =>
  Boolean(sow?.changeOrder) ||
  ((cap?.type === "B" || cap?.type === "D") &&
    Boolean(activeProposal?.parentProposalId));

const evaluateCapSimplifiedExperience = ({
  offerReviewSimplifiedEnabled,
  presentationTargets,
  createdDateCutoff,
  state,
  utility,
  sow,
  activeProposal,
  cap,
  isNonSolar,
  isOnlyBattery,
  isOnlyEvCharger,
  additionalSystem,
}: EvaluateCapSimplifiedExperienceArgs): SimplifiedExperienceDecision => {
  const gate = evaluateSimplifiedPresentationGate({
    offerReviewSimplifiedEnabled,
    presentationTargets,
    state,
    utility,
    createdDateCutoff,
    subjectCreatedAt: activeProposal?.createdDate,
  });

  if (!gate.show) {
    return gate;
  }

  if (additionalSystem) {
    return simplifiedExperienceIneligible("SECOND_SYSTEM");
  }

  if (!hasCapSowAnnualSelfSupply(sow)) {
    return simplifiedExperienceIneligible("MISSING_SELF_SUPPLY");
  }

  const isChangeOrder = getCapIsChangeOrder({ sow, cap, activeProposal });

  if (isChangeOrder && !hasCapSowOriginalSelfSupply(sow)) {
    return simplifiedExperienceIneligible(
      "CHANGE_ORDER_MISSING_ORIGINAL_SELF_SUPPLY"
    );
  }

  if (isNonSolar || isOnlyBattery || isOnlyEvCharger) {
    return simplifiedExperienceIneligible("NON_SOLAR_OR_STORAGE_ONLY");
  }

  if (!capProposalHasSolarAndBattery(activeProposal)) {
    return simplifiedExperienceIneligible("NOT_SOLAR_AND_BATTERY_PACKAGE");
  }

  return gate;
};

type CapSimplifiedReadiness = {
  isCapPage: boolean;
  isCapPageLoading: boolean;
  flagsInitialised: boolean;
  flagsReady: boolean;
};

const getCapSimplifiedDecision = (
  {
    isCapPage,
    isCapPageLoading,
    flagsInitialised,
    flagsReady,
  }: CapSimplifiedReadiness,
  eligibilityArgs: EvaluateCapSimplifiedExperienceArgs
): SimplifiedExperienceDecision => {
  if (!isCapPage) {
    return { show: false, reason: null };
  }
  if (isCapPageLoading) {
    return simplifiedExperienceIneligible("CAP_PAGE_LOADING");
  }
  if (!flagsInitialised) {
    return simplifiedExperienceIneligible("FLAGS_NOT_INITIALISED");
  }
  if (!flagsReady) {
    return simplifiedExperienceIneligible("FLAGS_LOADING");
  }
  return evaluateCapSimplifiedExperience(eligibilityArgs);
};

export {
  evaluateCapSimplifiedExperience,
  getCapIsChangeOrder,
  getCapSimplifiedDecision,
};
export type { EvaluateCapSimplifiedExperienceArgs };
